import * as React from "react";
import { v4 as uuidv4 } from "uuid";
import { formSetValue, useCpFormValue } from "../../../gvc/testNewForm/utils";
import { CpInputContainerImage } from "./CpInputContainerImage";
import { CpFormItem } from "../../../gvc/testNewForm/CpFormItem";
import { CpLabel } from "../../../gvc/testNewForm/CpLabel";
import { CpInput } from "../../../gvc/testNewForm/CpInput";
import { CpErrorWarning } from "../../../gvc/testNewForm/CpErrorWarning";
import { CpSelect } from "../../../gvc/testNewForm/CpSelect";
import { CpArrayObject } from "../../../gvc/testNewForm/CpArrayObject";
import { CpArrayItemContainerPort } from "./CpArrayItemContainerPort";
import { CpSwitch } from "../../../gvc/testNewForm/CpSwitch";
import { CpInputNumber } from "../../../gvc/testNewForm/CpInputNumber";
import { cloneDeep, get } from "lodash";
import { store } from "../../../../store";
import { useCpFormContext } from "../../../../components/form/useCpFormContext";
import { CpLabelTooltip } from "../../../gvc/testNewForm/CpLabelTooltip";
import { CpTooltip } from "../../../gvc/testNewForm/CpTooltip";
import { CpLabelDirtyIndicator } from "../../../gvc/testNewForm/CpLabelDirtyIndicator";
import { Only } from "../only";

interface Props {
  index: number;
}
export const WorkloadContainerImage: React.FC<Props> = React.memo(({ index }) => {
  const { id } = useCpFormContext();
  const type = useCpFormValue(`type`);

  const servesTraffic = useCpFormValue(`containers.${index}.servesTraffic`);
  React.useEffect(() => {
    if (!servesTraffic) {
      return;
    }

    const form = store.getState().form.instances[id];
    const values = form.values;
    const _containers = get(values, `containers`);
    const containers = cloneDeep(_containers);

    for (const containerIndexStr in containers) {
      const containerIndex = Number(containerIndexStr);
      if (containerIndex === index) {
        continue;
      }
      const container = containers[containerIndex];
      container.servesTraffic = false;
      formSetValue(id, `containers.${containerIndex}`, container);
    }
  }, [servesTraffic]);

  return (
    <>
      <CpInputContainerImage index={index} />
      <CpFormItem>
        <CpLabel name={`containers.${index}.name.value`}>
          Name
          <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
            <CpLabelDirtyIndicator name={`containers.${index}.name.value`} />
          </Only>
        </CpLabel>
        <CpInput
          name={`containers.${index}.name.value`}
          onChange={() => {
            formSetValue(id, `containers.${index}.name.isManual`, true);
          }}
          style={{ maxWidth: 600 }}
        />
        <CpErrorWarning name={`containers.${index}.name.value`} />
      </CpFormItem>
      {type === "serverless" ? (
        <>
          <CpFormItem>
            <CpLabelTooltip>
              <CpLabel name={`containers.${index}.servesTraffic`}>
                Serves Traffic
                <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                  <CpLabelDirtyIndicator name={`containers.${index}.servesTraffic`} />
                </Only>
              </CpLabel>
              <CpSwitch name={`containers.${index}.servesTraffic`} />
              <CpTooltip
                tooltipProps={{
                  title: `Serverless workloads allow only one container to serve traffic, and on a single port`,
                }}
              />
            </CpLabelTooltip>
            <CpErrorWarning name={`containersservestrafficroot`} />
          </CpFormItem>
          {servesTraffic ? (
            <CpFormItem>
              <CpLabelTooltip>
                <CpLabel name={`containers.${index}.ports.${0}.number`}>
                  Port
                  <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                    <CpLabelDirtyIndicator name={`containers.${index}.ports.${0}`} />
                  </Only>
                </CpLabel>
                <CpTooltip
                  tooltipProps={{
                    title: `The port in the container which receives external traffic or traffic from other workloads.`,
                  }}
                />
              </CpLabelTooltip>
              <div className="flex items-start gap-4">
                <CpSelect
                  name={`containers.${index}.ports.${0}.protocol`}
                  options={[
                    { label: "HTTP", value: "http" },
                    { label: "HTTP2", value: "http2" },
                    { label: "GRPC", value: "grpc" },
                    { label: "TCP", value: "tcp" },
                  ]}
                  placeholder={`Protocol`}
                  style={{ width: 140 }}
                />
                <CpInputNumber
                  name={`containers.${index}.ports.${0}.number`}
                  style={{ width: 140 }}
                  placeholder="Port Number"
                />
              </div>
              <CpErrorWarning name={`containers.${index}.portsroot`} />
            </CpFormItem>
          ) : null}
        </>
      ) : null}
      {type === "standard" || type === "stateful" ? (
        <div style={{ maxWidth: 450 }}>
          <CpArrayObject
            label={`Ports`}
            description={`Add ports to receive internal and external traffic.`}
            name={`containers.${index}.ports`}
            labels={[`Protocol`, `Number`]}
            factoryFn={() => ({ id: uuidv4(), protocol: "http", number: "" })}
            dirtyIndicator={{
              isEnabled: true,
              only: {
                name: "meta.isCreate",
                checkFn: (v) => v === false,
              },
            }}
          >
            <CpArrayItemContainerPort />
          </CpArrayObject>
        </div>
      ) : null}
    </>
  );
});
